/**
 * Dialog for assessors to enter and save notes during a SIM session.
 *
 * @module components/sim/takeSim/AssessorNotes
 * @author Covintus
 * @author Snowfly
 * @version 1.0.0
 */
import React from "react";
import {
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    TextField,
    Tooltip
} from "@material-ui/core";
import DashboardPermissions from "../../../permissions/dashboard";
import permissionCheck from "../../../utils/permissionCheck";
import PropTypes from "prop-types";

/**
 * Renders a dialog component for managing assessor notes with options for adding, editing, and viewing notes.
 *
 * @param {Object} options - The parameters for the AssessorNotes component.
 * @param {Object} options.classes - CSS classes for custom styling of the dialog and its child components.
 * @param {function} options.onClose - Callback function to close the dialog.
 * @param {boolean} options.assessorNotes - Determines whether the dialog is open or closed.
 * @param {string} options.noteStatus - Represents the current status of the note, e.g., 'add' or 'edit'.
 * @param {function} options.setNotes - Callback function to update the state of the note content.
 * @param {string} options.notes - The current content of the notes being managed.
 * @param {function} options.onAdd - Callback function executed when adding a new note.
 * @param {function} options.onEdit - Callback function executed when editing an existing note.
 */
const AssessorNotes = ({
    classes,
    onClose,
    assessorNotes,
    noteStatus,
    setNotes,
    notes,
    onAdd,
    onEdit,
}) => {
    return (
        <Dialog
            onClose={onClose}
            open={assessorNotes}
            classes={{paper: classes.dialog}}
            fullWidth
            maxWidth="sm"
        >
            <DialogTitle>Assessor Notes</DialogTitle>
            <DialogContent>
                <div style={{padding: "0.5rem", width: "27vw"}}>
                    <TextField
                        inputprops={{
                            disableUnderline: true,
                            style: { fontSize: 14, paddingLeft: 8 },
                        }}
                        fullWidth
                        multiline
                        minRows={5}
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        className={classes.textArea}
                        placeholder="Type your note here"
                        disabled={
                            !permissionCheck(
                                DashboardPermissions.assessor_notes.update.task,
                                DashboardPermissions.assessor_notes.update.type
                            ) &&
                            !permissionCheck(
                                DashboardPermissions.assessor_notes.create.task,
                                DashboardPermissions.assessor_notes.create.type
                            ) &&
                            permissionCheck(
                                DashboardPermissions.assessor_notes.view.task,
                                DashboardPermissions.assessor_notes.view.type
                            )
                        }
                    />
                    <Button
                        size="small"
                        style={{
                            fontWeight: 600,
                            padding: "3px 11px",
                            backgroundColor: "#F8DEE7",
                            float: "right",
                            color: "#A55B73",
                            margin: "8px 0px",
                        }}
                        onClick={onClose}
                    >
                        Cancel
                    </Button>
                    {noteStatus === "edit" ? (
                        <Tooltip
                            title={
                                !permissionCheck(
                                    DashboardPermissions.assessor_notes.update.task,
                                    DashboardPermissions.assessor_notes.update.type
                                )
                                    ? "You don't have permission to update"
                                    : ""
                            }
                            placement="top"
                        >
                                <span className={classes.editSpan}>
                                    <Button
                                        size="small"
                                        className={classes.button}
                                        disabled={
                                            !permissionCheck(
                                                DashboardPermissions.assessor_notes.update.task,
                                                DashboardPermissions.assessor_notes.update.type
                                            )
                                        }
                                        onClick={onEdit}
                                    >
                                        Save
                                    </Button>
                                </span>
                        </Tooltip>
                    ) : noteStatus === "add" ? (
                        <Tooltip
                            title={
                                !permissionCheck(
                                    DashboardPermissions.assessor_notes.create.task,
                                    DashboardPermissions.assessor_notes.create.type
                                )
                                    ? "You don't have permission to create"
                                    : ""
                            }
                            placement="top"
                        >
                                <span className={classes.editSpan}>
                                    <Button
                                        size="small"
                                        className={classes.button}
                                        disabled={
                                            !permissionCheck(
                                                DashboardPermissions.assessor_notes.create.task,
                                                DashboardPermissions.assessor_notes.create.type
                                            )
                                        }
                                        onClick={onAdd}
                                    >
                                        Save
                                    </Button>
                                </span>
                        </Tooltip>
                    ) : null}
                </div>
            </DialogContent>
        </Dialog>
    );
}

/**
 * Defines the PropTypes for the AssessorNotes component.
 * This property serves to validate the types of props passed to the component,
 * ensuring the correct structure and data types are adhered to.
 */
AssessorNotes.propTypes = {
    classes: PropTypes.object,
    onClose: PropTypes.func,
    assessorNotes: PropTypes.bool,
    noteStatus: PropTypes.string,
    setNotes: PropTypes.func,
    notes: PropTypes.string,
    onAdd: PropTypes.func,
    onEdit: PropTypes.func,
};


export default AssessorNotes;
